import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { TextareaModule } from 'primeng/textarea';

interface Form {
  inputUrlOrTextList: FormArray<FormControl<string | null>>;
}

@Component({
  selector: 'app-sources-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputGroupModule,
    InputGroupAddonModule,
    TextareaModule,
    FloatLabelModule,
  ],
  templateUrl: './sources-form.component.html',
})
export class SourcesFormComponent implements OnInit {
  @Input() initialSources: (string | null)[] = [null];
  @Input() maxSourceItems = 5;
  @Input() readonly = false;
  @Output() sourcesChange = new EventEmitter<(string | null)[]>();
  @Output() validityChange = new EventEmitter<boolean>();

  form!: FormGroup<Form>;

  get inputUrlOrTextArray() {
    return this.form.get('inputUrlOrTextList') as FormArray<
      FormControl<string | null>
    >;
  }

  constructor(private formBuilder: FormBuilder) {}

  ngOnInit() {
    this.createForm();
  }

  private createForm() {
    // Create form controls from initial sources or default to one empty control
    const controls =
      this.initialSources.length > 0
        ? this.initialSources.map(source =>
            this.formBuilder.control<string>(source),
          )
        : [this.formBuilder.control<null>(null)];

    this.form = this.formBuilder.group({
      inputUrlOrTextList: this.formBuilder.array(controls),
    });

    // Emit changes when form value changes
    this.form.valueChanges.subscribe(() => {
      this.sourcesChange.emit(this.inputUrlOrTextArray.value.filter(Boolean));
      this.validityChange.emit(this.form.valid);
    });

    // Emit initial validity
    this.validityChange.emit(this.form.valid);
  }

  addSource() {
    if (this.inputUrlOrTextArray.length >= this.maxSourceItems) {
      return;
    }

    this.inputUrlOrTextArray.push(
      this.formBuilder.control<string | null>(null),
    );
  }

  removeSource(idx: number) {
    if (this.inputUrlOrTextArray.length === 1) {
      this.inputUrlOrTextArray.at(idx).setValue(null);
    } else {
      this.inputUrlOrTextArray.removeAt(idx);
    }
  }

  getSources(): (string | null)[] {
    return this.inputUrlOrTextArray.value;
  }

  isFormValid(): boolean {
    return this.form.valid;
  }
}
